import { server } from '../core/server/axios';
import { BACKEND_URL } from '@/core/config';

/**
 * Инкассация — перемещение денег и ценностей между отделениями через инкассатора:
 * кассир-отправитель → инкассатор → кассир-получатель. Обе передачи одинаковы: кассир показывает QR, инкассатор
 * сканирует его и называет код, кассир вводит код (pickup/confirm — отправитель, delivery/confirm — получатель).
 * Код приёмки завершает заявку: подготовленные суммы зачисляются получателю, пересчёта нет (ОФД-взнос — в ответе).
 *
 * Бэк ECASH. Заявки создаёт только внешний POST /incassation/intake — из интерфейса создания нет.
 * Даты приходят в UTC с суффиксом Z. Для показа — `number` (INC-000123), для запросов — `id`.
 *
 * Допустимые шаги решает сервер: в карточке приходит `allowedActions`, фронт показывает только их.
 * Каждое действие — одна неидемпотентная операция: без автоповторов. В запросе действия уходит `version`,
 * устаревшую карточку сервер отсекает кодом 409 — её нужно перечитать. Ответ действия карточку не заменяет:
 * после действия карточка перечитывается через `getOne`. Ошибки приходят как `{ code, message }`.
 */

const INCASSATION = '/incassation';

export enum IncassationStatus {
    PendingTreasurer = 0,
    Created = 1,
    Preparing = 2,
    WaitingForPickup = 3,
    InTransit = 4,
    WaitingForAcceptance = 5,
    DiscrepancyReview = 6,
    Completed = 7,
    Cancelled = 8,
    CashierRejected = 9,
}

export type IncassationSourceCode = 'EXTERNAL' | 'MANUAL' | 'FORECAST' | 'LIMITS';

export const INCASSATION_SOURCE_LABEL: Record<IncassationSourceCode, string> = {
    EXTERNAL: 'Внешняя система',
    MANUAL: 'Вручную',
    FORECAST: 'Прогноз',
    LIMITS: 'Лимиты',
};

/** Действия над заявкой; значение — путь после `/incassation/{id}/` и имя в `allowedActions` */
export enum IncassationAction {
    /** Казначей подтверждает заявку из внешней системы */
    Confirm = 'treasurer/confirm',
    /** Казначей правит суммы до подтверждения */
    Correct = 'treasurer/correct',
    /** Казначей ставит очередь объезда на день (1 — первой); занятый номер меняется местами */
    Queue = 'treasurer/queue',
    Accept = 'accept',
    Reject = 'reject',
    Cancel = 'cancel',
    Prepare = 'prepare',
    /** Инкассатор сканирует QR: идёт не по id заявки, а в общий POST /incassation/scan */
    Scan = 'scan',
    /** Показать QR текущего шага на экране кассира: приходит только в allowedActions, вызывается через getQr */
    Qr = 'qr',
    PickupConfirm = 'pickup/confirm',
    Arrive = 'arrive',
    DeliveryConfirm = 'delivery/confirm',
    Receive = 'receive',
    IncidentReview = 'incident/review',
    IncidentResolve = 'incident/resolve',
    OtpReset = 'otp/reset',
}

/** Какой из двух кодов: передача инкассатору или приёмка у инкассатора */
export type IncassationPurpose = 'pickup' | 'delivery';

/** kind: 0 — деньги, 1 — ценность */
/** Денежная позиция (kind 0) или ценность (kind 1); суммы в действиях уходят по `id` позиции */
export interface IncassationItem {
    id: number;
    kind: 0 | 1;
    currencyCode?: string | null;
    fromCashId?: number | null;
    toCashId?: number | null;
    amount?: number | null;
    rate?: number | null;
    amountKzt?: number | null;
    /** Себестоимость (WAC) отправителя на момент заявки; null — не передавали */
    avgRate?: number | null;
    /** Количество × (курс − себестоимость), считает сервер */
    profit?: number | null;
    /** Примечание строки: обоснование рекомендации */
    note?: string | null;
    preparedAmount?: number | null;
    receivedAmount?: number | null;
    name?: string | null;
    qty?: number | null;
}

export interface IncassationTransition {
    at: string;
    fromStatus: IncassationStatus | null;
    fromStatusCode: string | null;
    toStatus: IncassationStatus;
    toStatusCode: string;
    toStatusLabel: string;
    actorLogin: string;
    actorRole: string | null;
    note?: string | null;
}

export interface IncassationIncident {
    /** Подпись: «Расхождение при приёмке» или «Повреждение пломбы» */
    type: string;
    /** Подпись: «Открыто», «На рассмотрении», «Закрыто» */
    status: string;
    typeCode?: number;
    statusCode?: number;
    comment?: string | null;
    resolution?: string | null;
    createdAt: string;
    resolvedAt?: string | null;
}

/**
 * Состояние кода текущего шага. Приходит только в статусах, где ждут код (3 и 5), иначе `null`.
 * Сам код в карточку не попадает: его показывает инкассатору сканирование QR.
 */
export interface IncassationOtp {
    purpose: IncassationPurpose;
    /** QR выпущен; пока нет, остальные поля пустые */
    issued: boolean;
    /** Инкассатор уже отсканировал QR, и код ему показан */
    codeRevealed: boolean;
    expiresAt?: string | null;
    attemptsLeft?: number | null;
    /** 5 неверных попыток: доступен только otp/reset руководителю */
    locked: boolean;
    /** Прошло 15 минут: QR выпускается заново через otp/reset */
    expired: boolean;
}

export interface IncassationRow {
    id: number;
    number: string;
    status: IncassationStatus;
    statusCode: string;
    statusLabel: string;
    isOverdue: boolean;
    fromDepId: number;
    fromDepName: string;
    /** Город отправителя: по нему реестр делится на вкладки, в нём же действует очередь объезда */
    fromCity?: string | null;
    fromKassaId: number | null;
    toDepId: number;
    toDepName: string;
    toCity?: string | null;
    toKassaId: number | null;
    plannedAt: string;
    /** Откуда пришла заявка */
    source?: IncassationSourceCode;
    /** Очередь объезда на день (1 — первой); `null` — не назначена. Ставит казначей */
    queueNumber?: number | null;
    createdAt: string;
    createdBy: string;
    sealNumber: string | null;
    bagNumber: string | null;
    collectorLogin: string | null;
    transitStartedAt: string | null;
    items: IncassationItem[];
}

export interface IncassationModel extends IncassationRow {
    comment?: string | null;
    /** Причина отмены или отклонения */
    reason?: string | null;
    sealIntact?: boolean | null;
    responsibleLogin?: string | null;
    arrivedAt?: string | null;
    incident?: IncassationIncident | null;
    transitions?: IncassationTransition[];
    allowedActions: IncassationAction[];
    /** Номера очереди для выбора, только когда в `allowedActions` есть Queue; `takenBy` — заявка, с которой номер поменяется */
    queueSlots?: { queue: number; takenBy?: string | null }[] | null;
    otp?: IncassationOtp | null;
    /** Уходит в каждом действии */
    version: number;
}

export interface IncassationFilter {
    status?: IncassationStatus | '';
    fromDepId?: number | null;
    toDepId?: number | null;
    /** Город отправителя; пусто — все города */
    city?: string;
    /** Номер или его часть */
    number?: string;
    /** Дата создания, полный ISO-момент в UTC */
    dateFrom?: string;
    dateTo?: string;
    overdueOnly?: boolean;
    page?: number;
    /** Не больше 200 */
    size?: number;
}

export interface IncassationListResult {
    total: number;
    page: number;
    size: number;
    items: IncassationRow[];
}

/** Плитки реестра */
export interface IncassationStats {
    inTransit: number;
    waitingForAcceptance: number;
    completedToday: number;
    discrepancy: number;
    overdue: number;
}

export interface IncassationAuditRow {
    id: number;
    incassationId: number;
    number: string;
    at: string;
    fromStatus: IncassationStatus | null;
    fromStatusLabel: string | null;
    toStatus: IncassationStatus;
    toStatusLabel: string;
    actorLogin: string | null;
    actorRole: string | null;
    note: string | null;
    geo: string | null;
}

export interface IncassationAuditResult {
    total: number;
    page: number;
    size: number;
    items: IncassationAuditRow[];
}

export interface Geo {
    lat: number;
    lon: number;
}

/** Сумма по позиции заявки: подготовлено (prepare), принято (receive) или исправленная заявленная (treasurer/correct) */
export interface IncassationItemAmount {
    itemId: number;
    amount?: number;
    /** Только treasurer/correct: курс сделки. Бэк должен принять его, пересчитать amountKzt и profit и записать в журнал */
    rate?: number;
    /** Только treasurer/correct: убрать валюту из заявки; последнюю убрать нельзя — заявку отменяют целиком */
    remove?: boolean;
    preparedAmount?: number;
    receivedAmount?: number;
}

/** Поля действий; координаты нужны физическим шагам: prepare, scan, pickup/confirm, arrive, delivery/confirm, receive */
export interface IncassationActionBody extends Partial<Geo> {
    reason?: string;
    comment?: string;
    code?: string;
    bagNumber?: string;
    sealNumber?: string;
    /** Все денежные позиции для prepare и receive; для treasurer/correct — только изменённые */
    items?: IncassationItemAmount[];
    /** pickup/confirm: пломба совпадает с записанной, иначе 400 */
    sealMatches?: boolean;
    /** receive: пломба цела, иначе заявка уходит на разбор с инцидентом */
    sealIntact?: boolean;
    resolution?: string;
    /** otp/reset */
    purpose?: IncassationPurpose;
    /** treasurer/queue: номер очереди, `null` снимает; в confirm/correct — задать вместе с шагом */
    queue?: number | null;
}

/** Операция ОФД, которую фронт проводит и печатает после шага (prepare, delivery/confirm); только KZT */
export interface IncassationOfd {
    /** exp — изъятие у отправителя, ins — взнос получателю */
    operation: 'exp' | 'ins';
    kassaId: number;
    sum: number;
    currencyCode: string;
}

/** Ответ действия: если шаг выпускает QR для показа второй стороне (arrive), токен приходит здесь */
export interface IncassationActionResult {
    qrToken?: string | null;
    /** Что провести в ОФД после шага; сервер ОФД не вызывает */
    ofd?: IncassationOfd[];
}

export interface QrResult {
    qrToken: string;
    expiresAt?: string | null;
}

export interface ScanResult {
    incassationId: number;
    purpose: IncassationPurpose;
    /** Код инкассатор показывает на экране, второй стороне — для подтверждения */
    code: string;
    expiresAt: string;
}

/** Строка пакета для `POST /incassation/intake`: все id по отделениям и по кассам-остаткам (`Cash.CashId`) нужной валюты */
export interface IncassationIntakeRow {
    Currency: string;
    Amount: number;
    /** Курс сделки */
    Rate: number;
    Tenge: number;
    FromDepId: number;
    FromCashId: number;
    ToDepId: number;
    ToCashId: number;
    /** Себестоимость (WAC), для прибыли */
    AvgRate?: number;
    /** До 500 символов */
    Note?: string;
    Source?: 'manual' | 'forecast' | 'limits';
}

export interface IncassationIntakeCreated {
    id: number;
    number: string;
    fromKassaId: number;
    toKassaId: number;
    itemsCount: number;
}

export interface IncassationIntakeResult {
    created: IncassationIntakeCreated[];
    /** Пакет с тем же ключом уже принимали — вернулись прежние инкассации */
    duplicates: IncassationIntakeCreated[];
    errors: { row: number; message: string }[];
}

export const incassationErrorText = (e: any, fallback = 'Не удалось выполнить действие'): string => (
    e?.response?.data?.message || e?.response?.data?.error || e?.message || fallback
);

export const incassationErrorStatus = (e: any): number | undefined => e?.response?.status;

export class IncassationController {
    async getList(filter: IncassationFilter, signal?: AbortSignal): Promise<IncassationListResult> {
        const res = await server.get(`${INCASSATION}/history`, {
            baseURL: BACKEND_URL,
            signal,
            params: {
                status: filter.status === '' ? undefined : filter.status,
                fromDepId: filter.fromDepId || undefined,
                toDepId: filter.toDepId || undefined,
                city: filter.city || undefined,
                number: filter.number || undefined,
                dateFrom: filter.dateFrom || undefined,
                dateTo: filter.dateTo || undefined,
                overdueOnly: filter.overdueOnly || undefined,
                page: filter.page ?? undefined,
                size: filter.size ?? undefined,
            },
            silentError: true,
        });

        return {
            total: res?.total ?? (res?.items || []).length,
            page: res?.page ?? 1,
            size: res?.size ?? (res?.items || []).length,
            items: res?.items || [],
        };
    }

    /**
     * Маршрутный лист города на день: подтверждённые заявки в порядке очереди казначея, без очереди — в конце.
     * Доступен инкассатору (только города его отделений) и казначею. `date` — любой момент нужного дня, ISO в UTC; пусто — сегодня.
     */
    async getRoute(city: string, date?: string, signal?: AbortSignal): Promise<IncassationRow[]> {
        const res = await server.get(`${INCASSATION}/route`, { baseURL: BACKEND_URL, signal, params: { city, date: date || undefined }, silentError: true });

        return res || [];
    }

    /** Плитки: в пути, ждут приёмки, исполнено сегодня, с расхождением, просрочено */
    async getStats(signal?: AbortSignal): Promise<IncassationStats> {
        return server.get(`${INCASSATION}/stats`, { baseURL: BACKEND_URL, signal });
    }

    /** Журнал действий по видимым заявкам, новые сверху; page с 1, size не больше 200 */
    async getAudit(page: number, size: number, signal?: AbortSignal): Promise<IncassationAuditResult> {
        const res = await server.get(`${INCASSATION}/audit`, { baseURL: BACKEND_URL, signal, params: { page, size } });

        return { total: res?.total ?? 0, page: res?.page ?? page, size: res?.size ?? size, items: res?.items || [] };
    }

    async getOne(id: number, signal?: AbortSignal): Promise<IncassationModel> {
        return server.get(`${INCASSATION}/${id}`, { baseURL: BACKEND_URL, signal });
    }

    /** Действие по заявке. `version` обязателен: на устаревшую карточку сервер ответит 409 */
    async act(id: number, action: IncassationAction, version: number, body: IncassationActionBody = {}): Promise<IncassationActionResult> {
        const res = await server.post(`${INCASSATION}/${id}/${action}`, { ...body, version }, { baseURL: BACKEND_URL });

        return res || {};
    }

    /**
     * QR текущего шага для показа на экране кассира: сервер выдаёт токен и привязывает к нему код.
     * Вызывается, когда токена нет в ответе действия (после prepare, otp/reset) или QR нужно показать снова.
     * Требует от бэка: повторный вызов в пределах срока отдаёт тот же токен, не сбрасывает код и попытки.
     */
    async getQr(id: number, purpose: IncassationPurpose): Promise<QrResult> {
        return server.post(`${INCASSATION}/${id}/qr`, { purpose }, { baseURL: BACKEND_URL });
    }

    /** Инкассатор сканирует QR: сервер сам определяет передачу или приёмку по токену */
    async scan(qrToken: string, geo: Partial<Geo>): Promise<ScanResult> {
        return server.post(`${INCASSATION}/scan`, { qrToken, ...geo }, { baseURL: BACKEND_URL });
    }

    /**
     * Создать инкассации из пакета перемещений: одна инкассация на маршрут (касса → касса), уходят казначею на согласование.
     * Пакет принимается целиком. `batchId` (X-Batch-Id) — один набор строк, один ключ: повтор не создаёт дубль.
     * Ошибки строк приходят в теле ответа 400 в `errors`; 409 — тот же пакет принимается параллельно.
     */
    async intake(rows: IncassationIntakeRow[], batchId: string): Promise<IncassationIntakeResult> {
        return server.post(`${INCASSATION}/intake`, rows, { baseURL: BACKEND_URL, headers: { 'X-Batch-Id': batchId }, silentError: true });
    }
}
